import { useEffect, useState } from 'react';
import { SLA_THRESHOLD_MS } from '../reducers/orderSlice.constants';

/**
 * Human-readable elapsed time. Stays short past a minute so it cannot outgrow
 * the space it sits in.
 */
export const formatElapsed = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
};

/**
 * How long a final order has been outstanding, re-rendering once a second.
 *
 * HITL-1513. Shared by the centre-top banner and the Finish Order tooltip so
 * the two can never disagree about the number — they are two views of one fact,
 * and an agent who reads "8s" in one place and "12s" in the other stops
 * trusting both.
 *
 * The number is the whole signal: a spinner at 3 s and at 19 s look identical,
 * and "an order is outstanding" is not actionable in the way "19 seconds and
 * still unconfirmed" is.
 *
 * Both ends are the client clock, so this stays internally consistent on a
 * machine whose clock is wrong — unlike ORDER_PROCESSING_METRIC, which mixed a
 * browser stamp with server ones and published negative durations because of it.
 *
 * @param sentAt client-stamped send time, or null when nothing is outstanding
 * @param isActive whether to keep ticking; false stops the interval
 */
const useFinalOrderElapsed = (
  sentAt: string | null,
  isActive: boolean
): { elapsedMs: number; isOverdue: boolean } => {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!isActive) return;

    setNowMs(Date.now());
    const interval = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isActive]);

  const elapsedMs = sentAt
    ? Math.max(0, nowMs - new Date(sentAt).getTime())
    : 0;

  return {
    elapsedMs,
    // The same threshold ORDER_PROCESSING_METRIC records as sla_breached, so
    // what the agent sees escalate is the same event the dashboard counts.
    isOverdue: elapsedMs > SLA_THRESHOLD_MS,
  };
};

export default useFinalOrderElapsed;
